import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, TrendingDown, TrendingUp, FileText, Shield, LogOut, Menu, X, UserCog, Building2, Settings, CarFront } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Layout = ({ children, user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', testid: 'menu-dashboard', adminOnly: false },
    { path: '/pessoas', icon: Users, label: 'Cadastros', testid: 'menu-pessoas', adminOnly: false },
    { path: '/bancos', icon: Building2, label: 'Bancos', testid: 'menu-bancos', adminOnly: false },
    { path: '/entradas-saidas', icon: TrendingUp, label: 'Entradas e Saídas', testid: 'menu-entradas-saidas', adminOnly: false },
    { path: '/pagar', icon: TrendingDown, label: 'Contas a Pagar', testid: 'menu-pagar', adminOnly: false },
    { path: '/receber', icon: TrendingUp, label: 'Contas a Receber', testid: 'menu-receber', adminOnly: false },
    { path: '/acerto-motoristas', icon: CarFront, label: 'Acerto Motoristas', testid: 'menu-acerto-motoristas', adminOnly: false },
    { path: '/relatorios', icon: FileText, label: 'Relatórios', testid: 'menu-relatorios', adminOnly: false },
    { path: '/configuracoes', icon: Settings, label: 'Configurações', testid: 'menu-configuracoes', adminOnly: true },
    { path: '/usuarios', icon: UserCog, label: 'Usuários', testid: 'menu-usuarios', adminOnly: true },
    { path: '/auditoria', icon: Shield, label: 'Auditoria', testid: 'menu-auditoria', adminOnly: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50/20 to-orange-50/20">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center space-x-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_financeiro-boracar/artifacts/hl13aoq6_BoraCar%20-%20Logo%2001.png" 
            alt="BoraCar" 
            className="h-10 w-auto"
          />
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-40 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-slate-200">
            <img 
              src="https://customer-assets.emergentagent.com/job_financeiro-boracar/artifacts/hl13aoq6_BoraCar%20-%20Logo%2001.png" 
              alt="BoraCar" 
              className="h-10 w-auto"
            />
          </div>

          {/* Menu */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-3 space-y-1">
              {menuItems
                .filter(item => !item.adminOnly || user?.role === 'admin')
                .map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={item.testid}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg shadow-red-500/30'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium text-sm">{item.label}</span>
                      {item.adminOnly && (
                        <Shield className="w-3 h-3 ml-auto" />
                      )}
                    </Link>
                  );
                })}
            </div>
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{user?.nome}</p>
                <p className="text-xs text-slate-600 truncate">{user?.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded">
                  {user?.role}
                </span>
              </div>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
            <div className="mt-3 text-center text-xs text-slate-500">
              Desenvolvido por <span className="font-semibold text-slate-700">Igor Ribeiro</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen">
        <div className="pt-16 lg:pt-0">
          <main className="p-6">
            {children}
          </main>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;